import React, { useState, useEffect, Fragment } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  useHistory,
  Redirect,
  useLocation,
  useParams,
} from "react-router-dom";
import "./index.scss";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { useSelector } from "react-redux";
import Host from "../../Components/Host";
import HelmetComponent from "../../Components/HelmetComponent";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import { getMetaByCountry } from "../../metaConfig";

function WarrantyPolicy() {
  const { userId, userToken, userEmail, userName, isMember, isPaymentMethod, languageCode, countryCode } = useSelector(
    (state) => state.auth
  );
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const [getSearchId, setSearchId] = useState("");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      // behavior: "smooth"
    });
  }, []);

  const [isMetaTitle, setMetaTitle] = useState("");
  useEffect(() => {
    const { title } = getMetaByCountry(countryCode);
    setMetaTitle(title);
  }, [countryCode]);

  return (
    <>
      <HelmetComponent title={"Warranty Policy"} metaTitle={isMetaTitle} />
      <div className="statisc-page">
        <div className="container static-container">
          <div className="row static-row">
            <div className="col-md-10">
              <div
                className="static-container-card"
                style={{
                  textAlign: "left",
                  padding: "20px",
                  background: "#fcfcfc",
                  border: "1px solid #eee",
                  borderRadius: "7px",
                }}
              >
                <h3 style={{ padding: "20px 0px 20px 0px", fontWeight: 300, fontSize: "1.4rem" }}>
                  {t("Warranty Policy")}
                </h3>

                <p>
                  At {Host?.domainName2}, we want customers to have clear information about warranty coverage before and
                  after purchasing a product.
                </p>
                <p>
                  This Warranty Policy explains how warranty coverage works for products purchased through{" "}
                  {window.location.hostname}, including warranty availability, coverage periods, exclusions, and how to
                  request warranty support.
                </p>

                <h4>1. Warranty Availability</h4>
                <p>Some products sold on {Host?.domainName2} may include a:</p>
                <p style={{ marginBottom: "5px" }}>• manufacturer warranty;</p>
                <p style={{ marginBottom: "5px" }}>• supplier warranty; or</p>
                <p style={{ marginBottom: "5px" }}>• seller warranty.</p>
                <p>Warranty availability depends on the individual product, brand, supplier, and product category.</p>
                <p>Not all products include a warranty.</p>
                <p>
                  Where a warranty is available, the applicable warranty information may be displayed on the product
                  page, provided with the product, or confirmed by our customer support team.
                </p>
                <p>
                  Customers are encouraged to review the warranty information shown on the product page before placing
                  an order.
                </p>

                <h4>2. Warranty Period</h4>
                <p>
                  The duration of warranty coverage varies depending on the product and the warranty provided by the
                  manufacturer, supplier, or seller.
                </p>
                <p>Where applicable, the warranty period will be stated:</p>
                <p style={{ marginBottom: "5px" }}>• on the product page;</p>
                <p style={{ marginBottom: "5px" }}>
                  • in the warranty card or documentation supplied with the product; or
                </p>
                <p style={{ marginBottom: "5px" }}>• by our support team upon request.</p>
                <p>
                  The warranty period generally begins from the date of delivery unless otherwise specified by the
                  manufacturer or warranty provider.
                </p>
                <p>{Host?.domainName2} does not represent that every product carries the same warranty duration.</p>

                <h4>3. What a Warranty May Cover</h4>
                <p>
                  Where warranty coverage applies, it generally relates to verified manufacturing defects, hardware
                  faults, or product failures occurring during normal intended use within the applicable warranty
                  period.
                </p>
                <p>Depending on the product and warranty provider, an approved warranty claim may result in:</p>
                <p style={{ marginBottom: "5px" }}>• inspection;</p>
                <p style={{ marginBottom: "5px" }}>• troubleshooting;</p>
                <p style={{ marginBottom: "5px" }}>• repair;</p>
                <p style={{ marginBottom: "5px" }}>• replacement;</p>
                <p style={{ marginBottom: "5px" }}>• replacement of a defective component; or</p>
                <p style={{ marginBottom: "5px" }}>• another remedy available under the applicable warranty.</p>
                <p>
                  The remedy available will depend on the nature of the fault, warranty terms, product availability,
                  inspection results, and the manufacturer or supplier's warranty conditions.
                </p>

                <h4>4. Warranty Exclusions</h4>
                <p>
                  Unless specifically covered by the applicable manufacturer, supplier, or seller warranty, warranty
                  coverage does not normally include:
                </p>
                <p style={{ marginBottom: "5px" }}>• accidental or physical damage;</p>
                <p style={{ marginBottom: "5px" }}>• cracked or broken screens;</p>
                <p style={{ marginBottom: "5px" }}>• water or liquid damage;</p>
                <p style={{ marginBottom: "5px" }}>• misuse, abuse, or negligence;</p>
                <p style={{ marginBottom: "5px" }}>• improper storage or handling;</p>
                <p style={{ marginBottom: "5px" }}>• damage caused by incorrect installation;</p>
                <p style={{ marginBottom: "5px" }}>
                  • electrical damage caused by an unsuitable power supply or voltage;
                </p>
                <p style={{ marginBottom: "5px" }}>• damage caused by unauthorized modification or repair;</p>
                <p style={{ marginBottom: "5px" }}>
                  • damage caused by accessories or components not approved for use with the product;
                </p>
                <p style={{ marginBottom: "5px" }}>• normal wear and tear;</p>
                <p style={{ marginBottom: "5px" }}>• cosmetic damage that does not affect normal operation;</p>
                <p style={{ marginBottom: "5px" }}>• consumable components subject to normal deterioration;</p>
                <p style={{ marginBottom: "5px" }}>• software issues not related to a hardware defect;</p>
                <p style={{ marginBottom: "5px" }}>
                  • loss or damage caused by viruses, malware, or third-party software;
                </p>
                <p style={{ marginBottom: "5px" }}>• lost or missing accessories;</p>
                <p style={{ marginBottom: "5px" }}>
                  • removed, damaged, or altered serial numbers or identification labels;
                </p>
                <p style={{ marginBottom: "5px" }}>• damage caused after delivery through customer handling.</p>
                <p>
                  Warranty coverage may also be rejected if the product has been opened, modified, repaired, or serviced
                  by an unauthorized third party where this invalidates the applicable manufacturer's or supplier's
                  warranty.
                </p>

                <h4>5. Damaged or Defective Items on Delivery</h4>
                <p>
                  Warranty claims are separate from claims relating to products that arrive damaged, defective,
                  incorrect, or not as described.
                </p>
                <p>
                  If a product arrives damaged, defective, incorrect, or not as described, please contact{" "}
                  {Host?.domainName2} within 3 days of delivery so that the issue can be reviewed under our Returns and
                  Refunds Policy.
                </p>
                <p>Customers should provide:</p>
                <p style={{ marginBottom: "5px" }}>• their order number;</p>
                <p style={{ marginBottom: "5px" }}>• clear photographs or video showing the issue;</p>
                <p style={{ marginBottom: "5px" }}>• photographs of the packaging where applicable; and</p>
                <p style={{ marginBottom: "5px" }}>• a description of the problem.</p>
                <p>
                  Depending on the circumstances, {Host?.domainName2} may arrange a return, replacement, repair, or
                  refund after verification.
                </p>

                <h4>6. How to Make a Warranty Claim</h4>
                <p>To request warranty assistance, contact {Host?.domainName2} customer support:</p>
                <p>Email: {Host?.email}</p>
                <p>Phone: {Host?.phone}</p>
                <p>Support Hours: {Host?.customerSupport}</p>
                <p>Please provide:</p>
                <p style={{ marginBottom: "5px" }}>• your {Host?.domainName2} order number;</p>
                <p style={{ marginBottom: "5px" }}>• the product name;</p>
                <p style={{ marginBottom: "5px" }}>• the product serial number, where applicable;</p>
                <p style={{ marginBottom: "5px" }}>• a description of the issue;</p>
                <p style={{ marginBottom: "5px" }}>
                  • photographs or video demonstrating the fault, where possible; and
                </p>
                <p style={{ marginBottom: "5px" }}>• any other information requested by our support team.</p>
                <p>Our team will review the information provided and advise you on the next steps.</p>

                <h4>7. Warranty Inspection</h4>
                <p>A product may need to be inspected before a warranty claim can be approved.</p>
                <p>
                  Inspection may be performed by {Host?.domainName2}, the product supplier, manufacturer, authorized
                  service center, or another approved service provider depending on the product and applicable warranty.
                </p>
                <p>Inspection is used to determine:</p>
                <p style={{ marginBottom: "5px" }}>• whether a genuine fault exists;</p>
                <p style={{ marginBottom: "5px" }}>• whether the fault is covered by the applicable warranty;</p>
                <p style={{ marginBottom: "5px" }}>
                  • whether there are signs of accidental damage, misuse, unauthorized repair, or other excluded
                  conditions; and
                </p>
                <p style={{ marginBottom: "5px" }}>• the appropriate warranty remedy.</p>
                <p>Submission of a warranty claim does not automatically guarantee repair, replacement, or refund.</p>

                <h4>8. Returning a Product for Warranty Service</h4>
                <p>
                  If a product needs to be physically returned for an approved warranty assessment, our support team
                  will provide the appropriate instructions.
                </p>
                <p>
                  Where {Host?.domainName2} approves and arranges the return, we may provide a return shipping label and
                  arrange collection through an authorized courier.
                </p>
                <p>
                  Customers should not send a product back without receiving instructions and approval from{" "}
                  {Host?.domainName2}.
                </p>
                <p>Products sent without authorization may not be accepted or may experience delays in processing.</p>

                <h4>9. Manufacturer or Authorized Service Center Warranty</h4>
                <p>
                  For some products, warranty service may be provided directly by the manufacturer or an authorized
                  service center.
                </p>
                <p>
                  Where applicable, {Host?.domainName2} may provide the customer with information required to contact
                  the relevant manufacturer, supplier, or authorized service provider.
                </p>
                <p>
                  Warranty procedures, repair times, replacement terms, and other conditions may vary according to the
                  applicable manufacturer's or supplier's warranty terms.
                </p>
                <p>
                  {Host?.domainName2} will provide reasonable assistance to customers in understanding and accessing the
                  warranty applicable to products purchased through our website.
                </p>

                <h4>10. Repairs and Replacements</h4>
                <p>
                  Where a warranty claim is approved, the available remedy will depend on the applicable warranty and
                  inspection findings.
                </p>
                <p>A product may be:</p>
                <p style={{ marginBottom: "5px" }}>• repaired;</p>
                <p style={{ marginBottom: "5px" }}>• replaced with the same product;</p>
                <p style={{ marginBottom: "5px" }}>• replaced with an equivalent product where appropriate; or</p>
                <p style={{ marginBottom: "5px" }}>
                  • handled through another remedy permitted under the applicable warranty.
                </p>
                <p>A replacement is subject to product availability.</p>
                <p>
                  The availability of a refund for a warranty claim will depend on the circumstances of the claim and
                  the applicable warranty and Returns and Refunds Policy.
                </p>

                <h4>11. Data on Electronic Devices</h4>
                <p>
                  Customers submitting laptops, mobile phones, tablets, computers, storage devices, or other electronic
                  products for warranty inspection or repair should back up their data before submitting the product.
                </p>
                <p>Repair, replacement, or diagnostic procedures may result in the deletion or loss of data.</p>
                <p>
                  {Host?.domainName2} is not responsible for loss of personal files, software, photographs, contacts,
                  passwords, or other data stored on a device submitted for warranty service, except where liability
                  cannot legally be excluded.
                </p>
                <p>
                  Customers should also remove passwords, security locks, SIM cards, memory cards, and personal
                  accessories before submitting a device where appropriate.
                </p>

                <h4>12. Accessories and Consumable Components</h4>
                <p>Accessories and consumable components may have different warranty terms from the main product.</p>
                <p>
                  Items such as batteries, cables, adapters, filters, cartridges, replaceable components, or other
                  consumable parts may be subject to separate or limited warranty coverage depending on the manufacturer
                  or supplier.
                </p>
                <p>Any applicable coverage will depend on the warranty terms associated with the specific product.</p>

                <h4>13. Relationship With Our Returns Policy</h4>
                <p>This Warranty Policy should be read together with our Returns, Warranty and Refunds Policy.</p>
                <p>
                  Returns relating to products that are damaged on delivery, defective on delivery, incorrect, or not as
                  described are handled according to the return eligibility requirements and timeframes stated in that
                  policy.
                </p>
                <p>
                  A warranty claim generally applies to an eligible product fault that arises during the applicable
                  warranty period after delivery.
                </p>

                <h4>14. Contact Information</h4>
                <p>For warranty questions or warranty claims, please contact:</p>
                <div style={{ fontSize: "14px" }}>
                  <strong>Trade Name: {Host?.domainName2}</strong>
                                    <br />
                  <strong>Trade License: {Host?.companyName}</strong>
                  <br />
                  <strong>License Number: {Host?.licenseNumber}</strong>
                </div>
                <p style={{ lineHeight: 2 }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: Host?.address,
                    }}
                  ></div>
                  <MailOutlineIcon style={{ fontSize: "18px" }} /> {Host?.email} <br />
                  <PhoneIcon style={{ fontSize: "18px" }} /> {Host?.phone}
                </p>
                <p>Support Hours: {Host?.customerSupport}</p>
                <p>Website: {window.location.hostname}</p>

                <p>&nbsp;</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default WarrantyPolicy;
